import { action } from '@ember/object';
import { task, timeout } from 'ember-concurrency';
import EditController from 'cio/pods/object/attributes/edit/controller';
import { ObjectIdentifierKey } from 'cio/utils/identifiers';
import ObjectModel from 'cio/models/object';
import { queryEncode } from 'cio/helpers/query';
import { inject as service } from '@ember/service';
import ObjectTypesService from 'cio/services/object-types';

export default class NewObjectController extends EditController {
  @service objectTypes!: ObjectTypesService;
  isNew = true;

  // Since object creation is async, we don't get immediate feedback
  // on whether and when the change actually gets persisted.
  // We issue a GET request after a timeout to see if the object
  // has been created yet.
  isObjectCreated = task(async () => {
    this.alerts.sticky('object.create', 'Creating your object…');

    await timeout(this.interval);

    try {
      let apiObject;
      // imitate ObjectAttributeCondition with this object's `ObjectIdentifierKey.ObjectId`
      let apiObjects = await this.store.query('object', {
        filters: queryEncode({
          and: [
            {
              object_attribute: {
                field: ObjectIdentifierKey.ObjectId,
                value: this.model.attributes[ObjectIdentifierKey.ObjectId],
                operator: 'eq',
                type_id: this.objectType.id,
              },
            },
          ],
        }),
        objectTypeId: this.objectType.id,
      });
      // The back end doesn't return an error here, but instead an empty array. In this case, we
      // throw our own error to trigger the 404 case in the catch below.
      if (apiObjects.length === 0) {
        throw { errors: [{ status: '404' }] };
      }
      apiObject = apiObjects.slice()[0] as ObjectModel;

      this.alerts.remove('object.create');
      this.attributesProxy?.reset();
      this.router.transitionTo('object', apiObject.id);

      return true;
    } catch (e) {
      this.alerts.warning('object.create', 'Your object will be added soon.');
      this.attributesProxy?.reset();
      this.router.transitionTo('objects.type', this.objectType.slug);
      return true;
    }
  });

  save = task(async (attributes: Record<string, any>, hiddenAttributes: Record<string, any>) => {
    this.model.setProperties({
      attributes,
      hiddenAttributes,
      identifiers: {
        [ObjectIdentifierKey.ObjectId]: attributes[ObjectIdentifierKey.ObjectId],
      },
    });

    try {
      await this.model.saveAndQueue({});
    } catch (err) {
      return this.processServerError(err);
    }

    this.objectType.updateSentAttributes(attributes);

    await this.isObjectCreated.perform();
  });

  @action
  reset() {
    this.model.rollbackAttributes();
    this.router.transitionTo('objects.type', this.objectType.slug);
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your controllers.
declare module '@ember/controller' {
  interface Registry {
    'objects.type.new': NewObjectController;
  }
}
